import { ART_PAL, ball, fade, mix, n, rng, shade, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Pot scene behind the game zone: a festive arena in the evening (a bright hall in light). Two spotlights hang in the
 * upper corners and throw soft cones that fade out before the wheel; curled streamers and confetti float in the side
 * bands only. The middle (the wheel) stays calm: a soft pool of light and nothing else. The cones are soft gradients
 * that start and end inside the stage — no beam, streamer or line runs under the head, the stats or the stake panel.
 * Motifs are marked `data-motif="<x>"` (tests keep them out of the middle 40%). No filters, no text, no coins.
 */
export function potScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sp${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;

  const top = P.dark ? mix(P.bg, P.purple, 0.2) : mix(P.purple, '#FFFFFF', 0.88);
  const mid = P.dark ? mix(P.bg, P.purple, 0.1) : mix(P.pink, '#FFFFFF', 0.93);
  const low = P.dark ? mix(P.bg, P.blue, 0.08) : mix(P.gold, '#FFFFFF', 0.86);
  const beam = P.dark ? mix(P.gold, '#FFFFFF', 0.4) : '#FFFFFF';
  const lampBody = P.dark ? mix(P.bg, P.purple, 0.35) : mix(P.slate, P.purple, 0.35);
  const gold = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.5);
  // confetti and streamers: the festive palette (no white/black pairs, no coins)
  const hues = [P.teal, gold, P.pink, P.purple, P.orange, P.sky].map((c) => (P.dark ? mix(c, P.bg, 0.2) : c));
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  const defs =
    `<defs>${vgrad(`${p}bg`, top, mid, low)}` +
    // a cone: bright at the lamp, gone before the wheel
    `<linearGradient id="${p}cn" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${beam}" stop-opacity="${o(0.2, 0.75)}"/>` +
    `<stop offset=".6" stop-color="${beam}" stop-opacity="${o(0.06, 0.25)}"/><stop offset="1" stop-color="${beam}" stop-opacity="0"/></linearGradient>` +
    ball(`${p}lp`, lampBody, 0.35, 0.4) +
    fade(`${p}lh`, beam, P.dark ? 0.45 : 0.9) +
    fade(`${p}pool`, P.dark ? P.purple : P.gold, P.dark ? 0.16 : 0.2) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.35 : 0.5) +
    `</defs>`;

  /** a spotlight hanging at (x, y), its cone turned by `rot` degrees (0 = straight down) */
  const spot = (x: number, y: number, s: number, rot: number) =>
    `<g data-motif="${Math.round(x)}">` +
    `<path d="M${n(x - 16 * s)} ${y}L${n(x - 110 * s)} ${n(y + 380 * s)}H${n(x + 110 * s)}L${n(x + 16 * s)} ${y}Z" fill="url(#${p}cn)" transform="rotate(${rot} ${x} ${y})"/>` +
    `<circle cx="${x}" cy="${y}" r="${n(46 * s)}" fill="url(#${p}lh)"/>` +
    `<g transform="rotate(${rot} ${x} ${y})"><rect x="${n(x - 20 * s)}" y="${n(y - 30 * s)}" width="${n(40 * s)}" height="${n(34 * s)}" rx="${n(8 * s)}" fill="url(#${p}lp)"/>` +
    `<ellipse cx="${x}" cy="${n(y + 4 * s)}" rx="${n(18 * s)}" ry="${n(6 * s)}" fill="${beam}" fill-opacity="${o(0.8, 1)}"/></g>` +
    `<path d="M${n(x - 26 * s)} ${n(y - 16 * s)}H${n(x + 26 * s)}" stroke="${gold}" stroke-width="${n(4 * s)}" stroke-linecap="round"/></g>`;
  /** a curled streamer: a soft wave from (x, y) downwards */
  const streamer = (x: number, y: number, len: number, amp: number, c: string, w: number) => {
    let d = `M${x} ${y}`;
    const k = 4;
    for (let i = 0; i < k; i++) {
      const y0 = y + (len * i) / k;
      const y1 = y + (len * (i + 1)) / k;
      const side = i % 2 ? -1 : 1;
      d += `C${n(x + amp * side)} ${n(y0 + (y1 - y0) * 0.3)} ${n(x + amp * side)} ${n(y0 + (y1 - y0) * 0.7)} ${x} ${n(y1)}`;
    }
    return `<path data-motif="${x}" d="${d}" fill="none" stroke="${c}" stroke-width="${w}" stroke-linecap="round" stroke-opacity="${o(0.55, 0.6)}"/>`;
  };
  /** confetti in a band [x0, x1] × [y0, y1]: small rotated rectangles and dots */
  const confetti = (seed: number, x0: number, x1: number, y0: number, y1: number, k: number, s: number) => {
    const r = rng(seed);
    let out = '';
    for (let i = 0; i < k; i++) {
      const x = x0 + r() * (x1 - x0);
      const y = y0 + r() * (y1 - y0);
      const c = hues[i % hues.length];
      out +=
        i % 3 === 2
          ? `<circle cx="${n(x)}" cy="${n(y)}" r="${n(3 * s)}" fill="${c}"/>`
          : `<rect x="${n(x - 5 * s)}" y="${n(y - 2.5 * s)}" width="${n(10 * s)}" height="${n(5 * s)}" rx="1" fill="${c}" transform="rotate(${Math.round(r() * 180)} ${n(x)} ${n(y)})"/>`;
    }
    return `<g data-motif="${Math.round((x0 + x1) / 2)}" fill-opacity="${o(0.55, 0.7)}">${out}</g>`;
  };

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.42)}" rx="${n(W * 0.4)}" ry="${n(H * 0.46)}" fill="url(#${p}pool)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.42)}" rx="${n(W * 0.3)}" ry="${n(H * 0.36)}" fill="url(#${p}calm)"/>`;

  // at 1440 × 900 the stage window is about x 220…1380, y 65…660 of the scene and the wheel covers x ≈ 530…1070: the
  // motifs keep inside x 270…470 / 1130…1330 there (nothing straddles the board edge); the outer bands show on wide screens
  if (wide)
    art +=
      spot(310, 120, 1, -24) +
      spot(1290, 120, 1, 24) +
      streamer(305, 250, 260, 20, hues[0] as string, 7) +
      streamer(1300, 240, 270, 20, hues[2] as string, 7) +
      streamer(440, 430, 170, 14, hues[1] as string, 5) +
      streamer(1165, 450, 160, 14, hues[3] as string, 5) +
      confetti(3, 270, 470, 210, 620, 16, 1.2) +
      confetti(8, 1130, 1330, 210, 620, 16, 1.2) +
      // outer bands, seen on wide screens only
      confetti(5, 40, 220, 160, 700, 10, 1.2) +
      confetti(6, 1380, 1560, 160, 700, 10, 1.2);
  else
    art +=
      spot(40, 50, 0.45, -24) +
      spot(360, 50, 0.45, 24) +
      streamer(22, 160, 150, 10, hues[0] as string, 4) +
      streamer(380, 150, 160, 10, hues[2] as string, 4) +
      confetti(3, 4, 56, 110, 360, 9, 0.8) +
      confetti(8, 344, 396, 110, 360, 9, 0.8);

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Pot: festive bunting — three small pennants on a string (repeat-x tile, 36 × 10). */
export function potAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const line = P.dark ? mix(P.bg, P.purple, 0.5) : tint(P.purple, 0.35);
  const c = [P.teal, P.dark ? P.gold : shade(P.gold, 0), P.pink];
  const flag = (x: number, col: string) =>
    `<path d="M${x} 1.5H${x + 8}L${x + 4} 8.5Z" fill="${col}" fill-opacity=".85"/>`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 10" width="36" height="10">` +
    `<path d="M0 1.5H36" stroke="${line}"/>` +
    flag(2, c[0] as string) +
    flag(14, c[1] as string) +
    flag(26, c[2] as string) +
    `</svg>`
  );
}

export default defineScene({ game: 'pot', scene: potScene, accent: potAccent });
